// Cast a champion ability: at a hex, from a tile / unit / city click while the
// player is in targeting mode (tryChampionCast), or at once from a stance /
// Ascend button (castNow). Framework-free: the sim and the snapshot come in
// as arguments so it is unit-testable.

import type { GameSnapshot, LegendAbilityRow, LegendUnitProp } from '@aod/protocol';
import { castHint } from './castHint.js';
import { abilitiesOf } from './championKinds.js';
import { legendRows } from './championRules.js';
import { useChampionUi } from './championStore.js';

export interface CastDeps {
  readonly snapshot: GameSnapshot | null;
  readonly activate: (unitId: string, slot: number, q: number, r: number) => string | null;
}

/** Pointy-top axial → board XZ (matches render/HexGrid.axialToWorld; inlined to keep this module three-free). */
function axialToWorld(q: number, r: number): [number, number, number] {
  return [Math.sqrt(3) * (q + r / 2), 0, 1.5 * r];
}

/** Height of the dragon's mouth above its tile, board units. */
const MOUTH_LIFT = 0.9;
const BLAST_LIFT = 0.3;
const GROUND_LIFT = 0.35;

/** VFX registry ids of the non-Firestorm abilities, by ability key. */
const ABILITY_EFFECT: Readonly<Record<string, { id: string; at: 'target' | 'caster' }>> = {
  withering: { id: 'legend:withering', at: 'target' },
  ascend: { id: 'legend:ascend', at: 'caster' },
};

/** The ability row a unit's slot refers to (kind from the unit's `legend` prop). */
export function abilityOf(
  snapshot: GameSnapshot | null,
  unitId: string,
  slot: number,
): LegendAbilityRow | null {
  const rows = legendRows(snapshot);
  const unit = snapshot?.scene.children?.find((n) => n.id === unitId);
  const kind = (unit?.props?.legend as LegendUnitProp | undefined)?.kind;
  if (!rows || !kind) return null;
  return abilitiesOf(rows, kind)[slot] ?? null;
}

/** Cast `slot` at (q, r); returns whether it was accepted. Errors are recorded for the panel. */
export function performCast(
  deps: CastDeps,
  unitId: string,
  slot: number,
  q: number,
  r: number,
): boolean {
  const ui = useChampionUi.getState();
  ui.setCastHits(null);
  const error = deps.activate(unitId, slot, q, r);
  const unit = deps.snapshot?.scene.children?.find((n) => n.id === unitId);
  const ability = abilityOf(deps.snapshot, unitId, slot);
  if (error) {
    ui.setError(error, ability?.key ?? null);
    return false;
  }
  const [ux, uy, uz] = unit?.transform.position ?? axialToWorld(q, r);
  const [tx, , tz] = axialToWorld(q, r);
  const key = ability?.key ?? 'firestorm';
  if (key === 'firestorm') {
    ui.pushFirestorm([ux, uy + MOUTH_LIFT, uz], [tx, BLAST_LIFT, tz]);
  } else if (key === 'bulwark') {
    // Garrison dust at the city hex, or the dug-in flash on the Golem's own hex.
    ui.pushEffect('legend:bulwark', [tx, GROUND_LIFT, tz]);
  } else {
    const fx = ABILITY_EFFECT[key];
    if (fx)
      ui.pushEffect(fx.id, fx.at === 'target' ? [tx, GROUND_LIFT, tz] : [ux, uy + GROUND_LIFT, uz]);
  }
  ui.setError(null);
  // An area ability that hit nothing under another player's units: say why (non-blocking).
  const radius = ability ? (ability.shape === 'area_damage' ? ability.radius : 0) : 1;
  const owner = (unit?.props?.ownerId as number | undefined) ?? -1;
  const hits = useChampionUi.getState().lastCastHits;
  ui.setNotice(radius > 0 ? castHint(deps.snapshot, owner, q, r, radius, hits) : null);
  return true;
}

/**
 * If the player is targeting an ability, cast it at (q, r) and report that
 * the click was consumed. A rejected cast keeps targeting on (so the player
 * can pick a nearer hex) and records the sim's rejection code for the panel.
 */
export function tryChampionCast(deps: CastDeps, q: number, r: number): boolean {
  const targeting = useChampionUi.getState().targeting;
  if (!targeting) return false;
  if (performCast(deps, targeting.unitId, targeting.slot, q, r)) {
    useChampionUi.getState().cancelTargeting();
  }
  return true;
}

/** Cast without aiming (stance buttons, Ascend); leaves any armed targeting alone. */
export function castNow(deps: CastDeps, unitId: string, slot: number, q: number, r: number) {
  useChampionUi.getState().cancelTargeting();
  return performCast(deps, unitId, slot, q, r);
}
